import React from 'react';
import { CreditCard, Wallet, Clock, AlertTriangle, CheckCircle, Landmark, Check, X, Minus } from 'lucide-react';
import { currencyRounded } from '../../utils/subscriberHelpers';
import StatusBadge from '../common/StatusBadge';

const fmtAmount = (v) => {
  const n = Number(v);
  if (Number.isNaN(n) || v == null || v === '') return null;
  return `₹${n.toLocaleString('en-IN')}`;
};

// Onboarding journey steps, in order. Every raw lender status (24 possible
// values across legacy pre/post-activation states, emandate sub-states, and
// the newer onboarding_*/failed_* states — see
// CREDIT_LINE_ACCOUNT_LENDER_STATUS_CHOICES in
// credit_line/models/credit_line_account_lender.py) is mapped to one of
// these 4 steps + a state (done/current/failed), so any status renders as a
// stepper position instead of a raw badge.
const STEPS = [
  { key: 'profile',  label: 'Profile' },
  { key: 'kyc',      label: 'KYC' },
  { key: 'emandate', label: 'Emandate' },
  { key: 'offer',    label: 'Offer' },
];

// status -> { step: index into STEPS, state: 'current' | 'failed' | terminal 'active'/'rejected' etc. }
const STATUS_STEP_MAP = {
  pre_approved: { step: -1, state: 'pending' },
  pending:      { step: 0,  state: 'current' },
  onboarding_profile:  { step: 0, state: 'current' },
  failed_profile:      { step: 0, state: 'failed' },
  kyc_done:             { step: 1, state: 'done' },
  onboarding_kyc:       { step: 1, state: 'current' },
  failed_kyc:           { step: 1, state: 'failed' },
  onboarding_emandate:            { step: 2, state: 'current' },
  failed_emandate:                { step: 2, state: 'failed' },
  mandate_done:                   { step: 2, state: 'done' },
  emandate_initialized:           { step: 2, state: 'current' },
  emandate_bank_approval_pending: { step: 2, state: 'current' },
  emandate_pending:               { step: 2, state: 'current' },
  emandate_active:                { step: 2, state: 'done' },
  emandate_failed:                { step: 2, state: 'failed' },
  emandate_cancelled:             { step: 2, state: 'failed' },
  emandate_expired:               { step: 2, state: 'failed' },
  emandate_rejected:              { step: 2, state: 'failed' },
  onboarding_offer: { step: 3, state: 'current' },
  failed_offer:     { step: 3, state: 'failed' },
  active:    { step: 3, state: 'done' },
  // suspended/closed only ever happen after the account was already fully
  // active — the Offer step genuinely completed — but they are NOT "all
  // good" states, so they must not render as a green done-checkmark.
  // suspended = blocked/paused, may be reactivated -> failed-style (red).
  // closed = permanently ended, not a failure of the onboarding journey
  // itself -> its own neutral/gray 'closed' state, not done and not failed.
  suspended: { step: 3, state: 'failed' },
  closed:    { step: 3, state: 'closed' },
  rejected:  { step: 3, state: 'failed' },
};

const getStepState = (status) => STATUS_STEP_MAP[status] || { step: -1, state: 'pending' };

const STEP_DOT_CLASSES = {
  done:    'bg-emerald-400 border-emerald-400',
  current: 'bg-white border-white',
  failed:  'bg-red-400 border-red-400',
  closed:  'bg-gray-400 border-gray-400',
  pending: 'bg-transparent border-white/30',
};

const STEP_LINE_CLASSES = {
  done: 'bg-emerald-400/60',
  filled: 'bg-white/60',
  empty: 'bg-white/15',
};

const Stepper = ({ status, description }) => {
  const { step: activeStep, state: activeState } = getStepState(status);

  return (
    <div>
      <div className="flex items-center">
        {STEPS.map((s, i) => {
          let dotState = 'pending';
          if (i < activeStep || (i === activeStep && activeState === 'done')) dotState = 'done';
          else if (i === activeStep) {
            if (activeState === 'failed') dotState = 'failed';
            else if (activeState === 'closed') dotState = 'closed';
            else dotState = 'current';
          }

          const lineFilled = i < activeStep || (i === activeStep && (activeState === 'done'));

          const isCurrent = dotState === 'current';

          return (
            <React.Fragment key={s.key}>
              <div className="flex flex-col items-center gap-1.5 shrink-0">
                <div className="relative w-5 h-5 flex items-center justify-center">
                  {isCurrent && (
                    <span className="absolute inline-flex h-full w-full rounded-full bg-white/60 animate-ping" />
                  )}
                  <div className={`relative w-5 h-5 rounded-full border-2 flex items-center justify-center ${STEP_DOT_CLASSES[dotState]} ${isCurrent ? 'shadow-[0_0_0_3px_rgba(255,255,255,0.3)]' : ''}`}>
                    {dotState === 'done' && <Check className="w-3 h-3 text-[#0F4C81]" strokeWidth={3} />}
                    {dotState === 'failed' && <X className="w-3 h-3 text-white" strokeWidth={3} />}
                    {dotState === 'closed' && <Minus className="w-3 h-3 text-white" strokeWidth={3} />}
                    {isCurrent && <span className="w-1.5 h-1.5 rounded-full bg-[#0F4C81]" />}
                  </div>
                </div>
                <span className={`text-[11px] whitespace-nowrap ${isCurrent ? 'text-white font-bold' : dotState === 'pending' ? 'text-blue-100/50 font-medium' : 'text-white font-medium'}`}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-0.5 flex-1 min-w-[16px] mx-1.5 mb-4 rounded-full ${lineFilled ? STEP_LINE_CLASSES.filled : STEP_LINE_CLASSES.empty}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {description && (
        <p className="text-xs text-blue-100 mt-2">{description}</p>
      )}
    </div>
  );
};

// Lender row on a dark gradient background — timeline/stepper treatment
// showing which onboarding step the applicant is currently at, with the
// live description as a caption underneath.
const LenderRow = ({ lender }) => {
  const creditLimit = fmtAmount(lender.credit_limit);
  const availableLimit = fmtAmount(lender.available_limit);
  const hasLimits = creditLimit || availableLimit;

  return (
    <div className="rounded-xl bg-white/10 backdrop-blur border border-white/10 px-4 py-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-sm font-semibold text-white capitalize">{lender.credit_lender?.name || '—'}</p>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-white/15 text-white ring-1 ring-inset ring-white/20">
          {lender.status_display || lender.status || '—'}
        </span>
      </div>
      {lender.status !== 'active' && (
        <Stepper status={lender.status} description={lender.description} />
      )}
      {hasLimits && (
        <div className="flex gap-6 text-xs text-blue-100 pt-1 border-t border-white/10">
          {creditLimit && <span className="pt-2">Credit Limit: <span className="font-semibold text-white">{creditLimit}</span></span>}
          {availableLimit && <span className="pt-2">Available: <span className="font-semibold text-white">{availableLimit}</span></span>}
        </div>
      )}
    </div>
  );
};

// Ayushpay Credit Snapshot — a single wide gradient hero banner with 5
// inline stats, deliberately distinct from Wallet Snapshot's tile grid so
// the two sections don't read as one repeated component in different
// colors. Also carries the "Current Available Credit" summary line instead
// of a repeated per-transaction column (see Phase 3 decision). Also shows
// CreditLineAccount status + per-lender status/description/limits below the
// stat grid — merged into this card rather than kept separate, per product
// decision, since the two data sources (ledger totals vs. account/lender
// status) both describe "the state of this subscriber's credit line" and
// read better as one card than two adjacent look-alikes.
const CreditSnapshotCard = ({ canView, creditSummary, loading, creditLineDetail, creditLineDetailLoading }) => {
  if (!canView) return null;

  if (loading) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl p-8 flex flex-col items-center gap-3">
        <div className="w-6 h-6 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm text-gray-400">Loading credit data…</p>
      </div>
    );
  }

  if (!creditSummary?.has_credit_line_account) return null;

  const lenders = creditLineDetail?.lenders || [];
  // contract: credit_line/models/credit_line_account_lender.py CREDIT_LINE_ACCOUNT_LENDER_STATUS_CHOICES ('active' is a literal choice value, same as STATUS_STEP_MAP above)
  const activeLender = lenders.find((lender) => lender.status === 'active');

  const stats = [
    { label: 'Max Credit', value: currencyRounded(creditSummary.max_credit || 0), icon: CreditCard },
    { label: 'Remaining', value: currencyRounded(creditSummary.remaining_credit || 0), icon: Wallet },
    { label: 'Active', value: creditSummary.active_count ?? 0, icon: Clock, color: 'text-amber-300' },
    { label: 'Overdue', value: creditSummary.overdue_count ?? 0, icon: AlertTriangle, color: 'text-red-500' },
    { label: 'Paid', value: creditSummary.paid_count ?? 0, icon: CheckCircle, color: 'text-emerald-300' },
  ];

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0F4C81] via-[#0F4C81] to-[#00A6A6] px-6 py-6 shadow-sm">
      <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-white/5 pointer-events-none" />
      <div className="absolute -bottom-16 -left-8 w-48 h-48 rounded-full bg-white/5 pointer-events-none" />

      <div className="relative flex items-center justify-between mb-6 flex-wrap gap-2">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-white/15 backdrop-blur flex items-center justify-center ring-1 ring-white/20">
            <CreditCard className="w-4.5 h-4.5 text-white" />
          </div>
          <h2 className="text-lg font-bold text-white">Ayushpay Credit Snapshot</h2>
        </div>
        {activeLender && (
          <StatusBadge status={activeLender.status} label={activeLender.status_display} />
        )}
      </div>

      <div className="relative grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6 divide-x divide-white/10">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <div key={stat.label} className={i > 0 ? 'pl-4 sm:pl-6' : ''}>
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-3.5 h-3.5 ${stat.color || 'text-white/60'}`} />
                <p className="text-xs font-medium text-blue-100 uppercase tracking-wide">{stat.label}</p>
              </div>
              <p className={`text-2xl font-bold leading-none ${stat.color || 'text-white'}`}>{stat.value}</p>
            </div>
          );
        })}
      </div>

      {!creditLineDetailLoading && creditLineDetail && !activeLender && (
        <div className="relative mt-6 pt-6 border-t border-white/10">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <h3 className="text-sm font-semibold text-white flex items-center gap-2">
              <Landmark className="w-4 h-4" />
              Credit Line Status
            </h3>
            <StatusBadge status={creditLineDetail.status} label={creditLineDetail.status_display} />
          </div>
          <div className="space-y-3">
            {lenders.length === 0 ? (
              <p className="text-xs text-blue-100/70 italic py-1">No lender attached to this credit line account yet</p>
            ) : (
              lenders.map((lender) => <LenderRow key={lender.id} lender={lender} />)
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditSnapshotCard;
